import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import SlideImage from '../Common/SlideImage'
import { GalleryWidget, Slide } from '@widgets/Gallery/types'
import { useFullScreenGallery } from '@hooks/useFullScreenGallery'
import useFullScreenImagePortal from '@hooks/useFullScreenImagePortal'
import CloseButton from '../Common/CloseButton'
import SlideFooter from '../Common/SlideFooter'
import { useGallerySlideClickTracking } from '../utils/useGalleryTracking'
import useShouldStretch from '@hooks/useShouldStretch'
import Credit from '@components/Gallery/Common/SlideImage/Credit'
import { StretchType } from '@contexts/storyTellingFullWidth'
import { printCSS } from '@utils/style'

const Wrapper = styled.div<{ aspectRatio: number; isFullScreen: boolean }>`
  ${({ aspectRatio, isFullScreen }) => css`
    padding-top: ${(isFullScreen ? 2 / 3 : aspectRatio) * 100}%;
    position: relative;
    ${printCSS(css`
      padding-top: 0;
    `)}
  `}
`

const SingleImageGalleryContainer = styled.div<{ isFullScreen: boolean }>`
  ${({ isFullScreen }) => css`
    ${isFullScreen &&
    css`
      max-width: 994px;
      width: 100%;
    `}
  `}
`

const ImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${printCSS(css`
    height: 234px;
    object-fit: cover;
    position: relative;
  `)}
`

const SingleImageGalleryPlaceholder = styled.div<StretchType>`
  ${({
    shouldStretchToHeaderWidth,
    shouldStretch,
    shouldStretchToCardWidth,
    theme: {
      measures: { stretch, stretchToFitCard },
    },
  }) => css`
    height: 100%;
    position: relative;
    ${printCSS(css`
      width: 50%;
      display: inline-block;
      break-inside: avoid;
      overflow: hidden;
    `)}

    ${shouldStretch &&
    css`
      ${stretch('left', shouldStretchToHeaderWidth)}
      ${stretch('right', shouldStretchToHeaderWidth)}
    `}
    ${shouldStretchToCardWidth &&
    css`
      ${stretchToFitCard('left')}
      ${stretchToFitCard('right')}
    `}
  `}
`

type SingleImageGalleryProps = Pick<GalleryWidget, 'title' | 'id'> & {
  slide: Slide
}

const SingleImageGallery: FunctionComponent<SingleImageGalleryProps> = ({
  slide,
  title: galleryTitle,
  id: galleryId,
}) => {
  const [aspectRatio, setAspectRatio] = useState<number>(2 / 3)
  const { image, credit, caption } = slide
  const { isFullScreen, setIsFullScreen } = useFullScreenGallery()
  const { portalNode, enterFullScreen, exitFullScreen, InPortal, OutPortal } =
    useFullScreenImagePortal()
  const stretch = useShouldStretch()

  const trackSlideClick = useGallerySlideClickTracking(
    1,
    galleryId,
    galleryTitle
  )

  const handleEnterFullScreen = useCallback(() => {
    if (!isFullScreen) {
      setIsFullScreen(enterFullScreen())
      trackSlideClick()
    }
  }, [setIsFullScreen, enterFullScreen, isFullScreen, trackSlideClick])

  const handleLeaveFullScreen = useCallback(() => {
    setIsFullScreen(!exitFullScreen())
  }, [setIsFullScreen, exitFullScreen])

  /** set the image aspect ratio so that a single image
   * extends in full height and is only constrained by
   * the available width
   */
  const calculateSingleImageAspectRatio = useCallback(() => {
    const freeCrop = image.crops?.['free']
    const originalDimensions = freeCrop
      ? { width: freeCrop.width, height: freeCrop.height }
      : { width: image.width, height: image.height }
    setAspectRatio(originalDimensions.height / originalDimensions.width)
  }, [image])

  useEffect(() => {
    calculateSingleImageAspectRatio()
  }, [calculateSingleImageAspectRatio])

  return !portalNode ? null : (
    <SingleImageGalleryPlaceholder {...stretch}>
      <InPortal node={portalNode}>
        <SingleImageGalleryContainer isFullScreen={isFullScreen}>
          <CloseButton onClose={handleLeaveFullScreen} />
          <Wrapper aspectRatio={aspectRatio} isFullScreen={isFullScreen}>
            <ImageWrapper>
              <SlideImage
                image={image}
                onClick={handleEnterFullScreen}
                shouldPreload={true}
              />
              {credit && <Credit text={credit.text} link={credit.link} />}
            </ImageWrapper>
          </Wrapper>
          <SlideFooter caption={caption} isSingleSlide={true} />
        </SingleImageGalleryContainer>
      </InPortal>
      {!isFullScreen && <OutPortal node={portalNode} />}
    </SingleImageGalleryPlaceholder>
  )
}

export default SingleImageGallery
